import type {
    BrandContentString, ContentStringCategory, ContentStringKey, ContentStringKeyInput,
} from '@/models/skin/ContentString';
import { getJwt, HttpError } from '../common';

const BASE_URL = import.meta.env.VITE_API_URL;

async function contentStringRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const hasBody = init?.body != null;
    const response = await fetch(`${BASE_URL}/${path}`, {
        ...init,
        headers: {
            'Authorization': `Bearer ${getJwt()}`,
            ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
            ...(init?.headers ?? {}),
        },
    });

    const contentType = response.headers.get('content-type') ?? '';
    const data = contentType.includes('json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) throw new HttpError(response.status, data);
    return data as T;
}

export const contentStringApi = {
    listCategories: () =>
        contentStringRequest<ContentStringCategory[]>('content-strings/categories'),

    listKeys: () =>
        contentStringRequest<ContentStringKey[]>('content-strings/keys'),

    listBrandOverrides: (brandId: string) =>
        contentStringRequest<BrandContentString[]>(`content-strings/brands/${brandId}`),

    upsertBrandOverride: (brandId: string, keyId: string, stringJson: string) =>
        contentStringRequest<void>(`content-strings/brands/${brandId}/keys/${keyId}`, {
            method: 'PUT',
            body: JSON.stringify({ stringJson }),
        }),

    // Removing the override is how a brand returns to the key's default.
    deleteBrandOverride: (brandId: string, keyId: string) =>
        contentStringRequest<void>(`content-strings/brands/${brandId}/keys/${keyId}`, { method: 'DELETE' }),

    // ---- Admin catalogue. Writes are super-admin only, enforced by PortalAPI. ----

    createCategory: (name: string) =>
        contentStringRequest<{ id: string }>('content-strings/categories', {
            method: 'POST',
            body: JSON.stringify({ name }),
        }),

    updateCategory: (id: string, name: string) =>
        contentStringRequest<void>(`content-strings/categories/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ name }),
        }),

    // Refused while the category still holds keys - surfaces as a 409.
    deleteCategory: (id: string) =>
        contentStringRequest<void>(`content-strings/categories/${id}`, { method: 'DELETE' }),

    createKey: (body: ContentStringKeyInput) =>
        contentStringRequest<{ id: string }>('content-strings/keys', {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    updateKey: (id: string, body: ContentStringKeyInput) =>
        contentStringRequest<void>(`content-strings/keys/${id}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    // Refused while any brand still overrides the key - surfaces as a 409.
    deleteKey: (id: string) =>
        contentStringRequest<void>(`content-strings/keys/${id}`, { method: 'DELETE' }),
};

export const contentStringKeys = {
    categories: () => ['content-string-categories'] as const,
    keys: () => ['content-string-keys'] as const,
    brandOverrides: (brandId: string) => ['content-string-overrides', brandId] as const,
};
